import { useContact } from '@revenuewell/front-end-bundle';
import { ListenerParameters } from 'pubnub';
import { useEffect, useState } from 'react';
import {
  LocationSwitchEventData,
  SwitchLocationDialog
} from '../../components/shared/dialog/switch-location-dialog/switch-location-dialog';
import { IMailboxWithLocation } from '../../services/mailbox-service/mailbox-service';
import { Message } from '../../services/pubnub-service';
import { EventJumpToMessage } from '../../types/event-bus';
import { formatUSPhoneNumber, getFullNameForGroup } from '../../utils/formatter';
import { makeProvider } from '../provider';
import { useContactState } from '../use-contacts';
import { useEventBus } from '../use-event-bus';
import { useLocations } from '../use-locations';
import { useNotification } from '../use-notification/use-notification';
import { usePubnub } from '../use-pubnub/use-pubnub';

export interface INativeNotification {
  title: string;
  body: string;
}

export const { MessengerNotificationProvider: MessengerNotificationProviderBase, useMessengerNotification } =
  makeProvider('MessengerNotification', () => {
    return {};
  });

export const MessengerNotificationProvider = ({ children }: { children: React.ReactNode }) => {
  const { isPermissionDefault, requestPermission, showNotification } = useNotification();
  const { client, mailboxes } = usePubnub();
  const { searchByNumber, contactCache } = useContactState();
  const { publish } = useEventBus();
  const { getContactsByPhoneNumber } = useContact();
  const { selectedLocation, locations, switchLocation } = useLocations();
  const [locationSwitchEvent, setLocationSwitchEvent] = useState<LocationSwitchEventData | null>(null);

  useEffect(() => {
    if (isPermissionDefault()) {
      requestPermission();
    }
  }, [isPermissionDefault, requestPermission]);

  useEffect(() => {
    if (!client) return;

    const listener: ListenerParameters = {
      message: async (data: any) => {
        const message = data.message as Message;
        if (message.messageType === 'incoming' && data.channel?.includes(message.from) && message.body !== 'C') {
          await searchByNumber(message.from);
          const contacts = contactCache[message.from];
          const fromDisplay = contacts?.data?.length
            ? getFullNameForGroup(contacts.data)
            : formatUSPhoneNumber(message.from);
          const mailbox = (mailboxes as IMailboxWithLocation[])
            .filter((mailbox) => data.channel.startsWith(mailbox.pubnubPrefix))
            .pop();

          showNotification(
            `New message from ${fromDisplay}`,
            { body: message.body },
            mailbox ? () => handleNotificationClick(message.from, mailbox.locationId) : undefined
          );
        }
      }
    };

    client.client.addListener(listener);

    return () => client.client.removeListener(listener);
  }, [client, mailboxes, contactCache, locations, selectedLocation]);

  const handleNotificationClick = async (fromNumber: string, locationId: number) => {
    if (!selectedLocation?.id) return;

    if (locationId == selectedLocation.id) {
      goToMessage(fromNumber, locationId);
    } else {
      const contacts = await getContactsByPhoneNumber(fromNumber);
      setLocationSwitchEvent({
        locationId: selectedLocation.id,
        locationName: locations.find((l) => l.id == locationId)?.name || '',
        patientPhone: fromNumber,
        patientName: contacts[0] ? `${contacts[0].firstName} ${contacts[0].lastName}` : undefined
      });
    }
  };

  const handleLocationSwitch = async (data: LocationSwitchEventData) => {
    setLocationSwitchEvent(null);
    await switchLocation(data.locationId);
    goToMessage(data.patientPhone, data.locationId);
  };

  const goToMessage = (phone: string, locId: number) => {
    publish({
      messageType: 'jumpToMessage',
      data: {
        phoneNumber: phone,
        locationId: locId
      } as EventJumpToMessage['data']
    });
  };

  return (
    <MessengerNotificationProviderBase>
      {children}
      {locationSwitchEvent && (
        <SwitchLocationDialog
          eventData={locationSwitchEvent}
          message="This message is for a patient in another location. Would you like to switch locations and go to their
            messages?"
          onSwitchLocation={handleLocationSwitch}
          onClose={() => setLocationSwitchEvent(null)}
        />
      )}
    </MessengerNotificationProviderBase>
  );
};
